import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, AlertCircle, Loader2, ChevronDown } from 'lucide-react';
import {
  SecurityEvent,
  SecurityEventType,
  SecurityEventSeverity,
  listSecurityEvents,
  getSecurityEventsMeta,
} from '../../services/securityEvents';

const EVENT_TYPE_LABELS: Record<SecurityEventType, string> = {
  failed_auth: 'Failed Auth',
  rate_limit_violation: 'Rate Limit Violation',
  ip_whitelist_violation: 'IP Whitelist Violation',
  unauthorized_access: 'Unauthorized Access',
  privilege_escalation: 'Privilege Escalation',
  suspicious_activity: 'Suspicious Activity',
  data_breach_attempt: 'Data Breach Attempt',
  tenant_isolation_violation: 'Tenant Isolation Violation',
  secrets_access_unauthorized: 'Secrets Access Unauthorized',
  policy_violation: 'Policy Violation',
};

function getSeverityBadgeClass(severity: SecurityEventSeverity): string {
  switch (severity) {
    case 'critical': return 'bg-red-100 text-red-700 border border-red-200';
    case 'high': return 'bg-orange-100 text-orange-700 border border-orange-200';
    case 'medium': return 'bg-amber-100 text-amber-700 border border-amber-200';
    case 'low': return 'bg-slate-100 text-slate-500 border border-slate-200';
    default: return 'bg-slate-100 text-slate-500 border border-slate-200';
  }
}

function getResponseBadgeClass(executed?: boolean): string {
  return executed
    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    : 'bg-slate-50 text-slate-400 border border-slate-200';
}

export default function SecurityEvents() {
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);

  const [eventTypes, setEventTypes] = useState<SecurityEventType[]>([]);
  const [severities, setSeverities] = useState<SecurityEventSeverity[]>([]);

  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [userIdFilter, setUserIdFilter] = useState('');

  useEffect(() => {
    getSecurityEventsMeta()
      .then((meta) => {
        setEventTypes(meta.eventTypes);
        setSeverities(meta.severities);
      })
      .catch(() => { /* filter dropdowns just stay empty; not fatal */ });
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await listSecurityEvents({
        eventType: (eventTypeFilter || undefined) as SecurityEventType | undefined,
        severity: (severityFilter || undefined) as SecurityEventSeverity | undefined,
        userId: userIdFilter || undefined,
      });
      setEvents(result.events);
      setNextCursor(result.nextCursor);
    } catch (err: any) {
      setError(err.message || 'Failed to load security events');
    } finally {
      setLoading(false);
    }
  }, [eventTypeFilter, severityFilter, userIdFilter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleLoadMore = async () => {
    if (!nextCursor) return;
    try {
      setLoadingMore(true);
      const result = await listSecurityEvents({
        cursor: nextCursor,
        eventType: (eventTypeFilter || undefined) as SecurityEventType | undefined,
        severity: (severityFilter || undefined) as SecurityEventSeverity | undefined,
        userId: userIdFilter || undefined,
      });
      setEvents(prev => [...prev, ...result.events]);
      setNextCursor(result.nextCursor);
    } catch (err: any) {
      setError(err.message || 'Failed to load more events');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <ShieldAlert size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Security Events</h1>
              <p className="text-slate-500 text-sm font-medium">
                Detected security events and automated responses (failed logins, rate-limit violations, suspensions)
              </p>
            </div>
          </div>
        </header>

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <AlertCircle size={16} className="shrink-0" /> {error}
          </div>
        )}

        {/* Filter + Table */}
        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center">
              <select value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)}
                className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                <option value="">All Event Types</option>
                {eventTypes.map(t => (
                  <option key={t} value={t}>{EVENT_TYPE_LABELS[t] || t}</option>
                ))}
              </select>
              <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}
                className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
                <option value="">All Severities</option>
                {severities.map(s => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Filter by user ID..."
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && fetchEvents()}
                className="flex-1 min-w-[200px] px-3 py-2 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
              <span className="ml-auto text-xs text-slate-400">{events.length} event{events.length !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-20 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          )}
          {!loading && events.length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <ShieldAlert size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No security events found</p>
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Time</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Type</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Severity</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Source IP</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">User</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Description</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Response</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {events.map(event => (
                    <tr key={event.eventId} className="hover:bg-slate-50/60 transition-colors align-top">
                      <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">
                        {new Date(event.timestamp).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-navy font-medium whitespace-nowrap">
                        {EVENT_TYPE_LABELS[event.eventType] || event.eventType}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getSeverityBadgeClass(event.severity)}`}>
                          {event.severity}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 font-mono text-xs whitespace-nowrap">{event.sourceIp}</td>
                      <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">{event.userId || '—'}</td>
                      <td className="px-5 py-3 text-slate-600 max-w-[360px]">
                        <p className="truncate" title={event.description}>{event.description}</p>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {event.responseAction && event.responseAction !== 'none' ? (
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${getResponseBadgeClass(event.responseExecuted)}`}>
                            {event.responseAction.replace(/_/g, ' ')}{event.responseExecuted ? '' : ' (pending)'}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {nextCursor && (
                <div className="flex justify-center py-4 border-t border-[#e2e8f0]">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="flex items-center gap-1.5 text-xs text-primary hover:text-primary-dark font-semibold transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? <Loader2 size={13} className="animate-spin" /> : <ChevronDown size={13} />}
                    Load more
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
